// Oracle parseable-body fetcher (Slice 17 dispatch arm).
//
// Per the slice, Oracle exposes:
//   - views               → `ALL_VIEWS.TEXT`
//   - scalar / TVF funcs  → `ALL_OBJECTS WHERE OBJECT_TYPE='FUNCTION'`
//                            re-assembled from `ALL_SOURCE` (Oracle stores
//                            routine bodies as one row per source line)
//   - default constraints → `ALL_TAB_COLUMNS.DATA_DEFAULT`
//   - check constraints   → `ALL_CONSTRAINTS WHERE constraint_type='C'`
//                            (`SEARCH_CONDITION` carries the expression text)
//
// Oracle's ALL_VIEWS.TEXT and ALL_TAB_COLUMNS.DATA_DEFAULT are LONG columns
// the IPC bridge surfaces as plain strings; routine bodies are re-built by
// concatenating ALL_SOURCE.TEXT rows ordered by LINE.
//
// We exclude the standard Oracle system schemas to keep the listing scoped to
// user-owned objects — the cross-schema visibility ALL_* views provide pulls
// in thousands of catalog entries otherwise.

import type {
  ObjectKind,
  PersistedObjectListing,
  QualifiedName,
} from '../../types';

import {
  asString,
  escapeSqlLiteral,
  fetchListingRows,
  fetchRows,
  fetchSourceRowsPaged,
  makeListing,
  type NavListingFetchOptions,
} from './queryHelper';

function listing(
  objectKind: PersistedObjectListing['objectKind'],
  schema: string,
  name: string,
  bodyText: string,
): PersistedObjectListing {
  return makeListing('oracle', objectKind, schema, name, bodyText);
}

const SYSTEM_OWNERS = `('SYS', 'SYSTEM', 'XDB', 'CTXSYS', 'MDSYS', 'OUTLN',
  'DBSNMP', 'WMSYS', 'APPQOSSYS', 'AUDSYS', 'GSMADMIN_INTERNAL', 'OJVMSYS',
  'ORDDATA', 'ORDSYS', 'OLAPSYS', 'EXFSYS', 'DVSYS', 'LBACSYS', 'DBSFWUSER',
  'GGSYS', 'PUBLIC')`;

const VIEWS_SQL = `SELECT OWNER AS "schema", VIEW_NAME AS "name", TEXT AS "body"
FROM ALL_VIEWS WHERE OWNER NOT IN ${SYSTEM_OWNERS}`;

// No inline ORDER BY: the paged fetch appends `ORDER BY OWNER, NAME, LINE`
// (its stable page key) followed by the row-limiting clause.
const FUNCTIONS_SOURCE_SQL = `SELECT OWNER AS "schema", NAME AS "name", LINE AS "line", TEXT AS "text"
FROM ALL_SOURCE WHERE TYPE = 'FUNCTION' AND OWNER NOT IN ${SYSTEM_OWNERS}`;

const DEFAULTS_SQL = `SELECT OWNER AS "schema", TABLE_NAME AS "table_name",
  COLUMN_NAME AS "column_name", DATA_DEFAULT AS "body"
FROM ALL_TAB_COLUMNS WHERE DATA_DEFAULT IS NOT NULL AND OWNER NOT IN ${SYSTEM_OWNERS}`;

const CHECKS_SQL = `SELECT OWNER AS "schema", CONSTRAINT_NAME AS "name", SEARCH_CONDITION AS "body"
FROM ALL_CONSTRAINTS WHERE CONSTRAINT_TYPE = 'C' AND OWNER NOT IN ${SYSTEM_OWNERS}
  AND GENERATED = 'USER NAME'`;

function assembleFunctionBodies(
  rows: readonly { schema: unknown; name: unknown; line: unknown; text: unknown }[],
): Map<string, { schema: string; name: string; body: string }> {
  const grouped = new Map<string, { schema: string; name: string; body: string }>();
  for (const row of rows) {
    const schema = asString(row.schema);
    const name = asString(row.name);
    const text = asString(row.text);
    if (!name) continue;
    const key = `${schema}.${name}`;
    const entry = grouped.get(key) ?? { schema, name, body: '' };
    entry.body += text;
    grouped.set(key, entry);
  }
  return grouped;
}

// Page keys. DIVERGENCE: the `ALL_*` dictionary views expose no surrogate id,
// so each feed orders by its qualified name. `ALL_VIEWS.TEXT` /
// `ALL_TAB_COLUMNS.DATA_DEFAULT` / `ALL_CONSTRAINTS.SEARCH_CONDITION` are LONG
// columns; paging them with `OFFSET … FETCH NEXT` follows the precedent the
// production Oracle schema fetcher already sets (`oracleSchemaIntrospection.ts`
// pages `DATA_DEFAULT` and `SEARCH_CONDITION` the same way), and a server that
// rejects it degrades to `navPagedRows.ts`'s un-paged retry rather than to an
// empty index. `ALL_SOURCE` is one row per
// SOURCE LINE, so its key is `(OWNER, NAME, LINE)` — and because
// `assembleFunctionBodies` runs AFTER every page has landed, a routine whose
// lines straddle a page boundary still reassembles in line order.
export async function listOracleParseableObjects(
  options: NavListingFetchOptions,
): Promise<readonly PersistedObjectListing[]> {
  const [viewRows, sourceRows, defaultRows, checkRows] = await Promise.all([
    fetchListingRows(options, {
      engine: 'oracle',
      label: 'oracle-views',
      sql: VIEWS_SQL,
      orderBy: 'OWNER, VIEW_NAME',
      dialect: 'offset-fetch',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'oracle',
      label: 'oracle-functions',
      sql: FUNCTIONS_SOURCE_SQL,
      orderBy: 'OWNER, NAME, LINE',
      dialect: 'offset-fetch',
      bodyColumns: ['text'],
    }),
    fetchListingRows(options, {
      engine: 'oracle',
      label: 'oracle-defaults',
      sql: DEFAULTS_SQL,
      orderBy: 'OWNER, TABLE_NAME, COLUMN_NAME',
      dialect: 'offset-fetch',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'oracle',
      label: 'oracle-checks',
      sql: CHECKS_SQL,
      orderBy: 'OWNER, CONSTRAINT_NAME',
      dialect: 'offset-fetch',
      bodyColumns: ['body'],
    }),
  ]);

  const out: PersistedObjectListing[] = [];

  for (const row of viewRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    out.push(listing('view', asString(row.schema), name, body));
  }

  const functions = assembleFunctionBodies(
    sourceRows.map((r) => ({ schema: r.schema, name: r.name, line: r.line, text: r.text })),
  );
  for (const fn of functions.values()) {
    if (!fn.body.trim()) continue;
    // Oracle scalar vs inline-TVF distinction is buried in the body's
    // `RETURN` clause; keep both under 'scalar-function' for v1 and let
    // Slice 19 (inline TVFs) refine the bucket if it carries enough signal
    // to matter for rename routing.
    out.push(listing('scalar-function', fn.schema, fn.name, fn.body));
  }

  for (const row of defaultRows) {
    const body = asString(row.body);
    const table = asString(row.table_name);
    const column = asString(row.column_name);
    if (!table || !column || !body) continue;
    out.push(listing('default-constraint', asString(row.schema), `${table}.${column}`, body));
  }

  for (const row of checkRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    out.push(listing('check-constraint', asString(row.schema), name, body));
  }

  return out;
}

/** Owners holding an object named `name` (already in catalog spelling) of
 *  any of `kinds` — the DDL invalidator's "where did this bare statement
 *  run?" probe. `ALL_OBJECTS` spans every owner the login can see. */
export async function listOracleSchemasHoldingObject(
  connectionId: string,
  database: string,
  name: string,
  kinds: readonly ObjectKind[],
): Promise<readonly string[]> {
  const types = Array.from(new Set(kinds.flatMap((kind) => ORACLE_TYPE_BY_KIND[kind] ?? [])));
  if (types.length === 0) return [];
  const sql = `SELECT DISTINCT OWNER AS "schema" FROM ALL_OBJECTS
    WHERE OBJECT_NAME = '${escapeSqlLiteral(name)}' AND OBJECT_TYPE IN (${types.map((t) => `'${t}'`).join(', ')})
      AND OWNER NOT IN ${SYSTEM_OWNERS}`;
  const rows = await fetchRows(connectionId, database, sql, 'oracle-schemas-of');
  return rows.map((r) => asString(r.schema)).filter((s) => s.length > 0);
}

const ORACLE_TYPE_BY_KIND: Partial<Record<ObjectKind, readonly string[]>> = {
  view: ['VIEW'],
  'inline-tvf': ['FUNCTION'],
  'scalar-function': ['FUNCTION'],
  procedure: ['PROCEDURE'],
  trigger: ['TRIGGER'],
};

export async function fetchOracleObjectBody(
  connectionId: string,
  database: string,
  objectKind: ObjectKind,
  objectName: QualifiedName,
): Promise<string | null> {
  // The owner arrives in catalog spelling (an unquoted DDL owner was folded
  // upstream; an index record or Object-Explorer name is the catalog's) —
  // upper-casing here would turn a quoted `"Sales"` into a miss.
  const s = escapeSqlLiteral(objectName.schema ?? '');
  const n = escapeSqlLiteral(objectName.name);

  if (objectKind === 'view') {
    const sql = `SELECT TEXT AS "body" FROM ALL_VIEWS
      WHERE OWNER = '${s}' AND VIEW_NAME = '${n}'`;
    const rows = await fetchRows(connectionId, database, sql, 'oracle-view');
    return asString(rows[0]?.body) || null;
  }

  // The listing files every Oracle function under `scalar-function` (see
  // `listOracleParseableObjects`), so an inline-tvf request has nothing to
  // answer — the DDL invalidator asks under both kinds and must not get the
  // same body twice.
  if (objectKind === 'inline-tvf') return null;

  if (objectKind === 'scalar-function') {
    // One row per source line — paged, so a long function comes back whole
    // (a fixed row cap used to cut it at 1,000 lines and index the stub).
    const sql = `SELECT TEXT AS "text" FROM ALL_SOURCE
      WHERE OWNER = '${s}' AND NAME = '${n}' AND TYPE = 'FUNCTION'`;
    const rows = await fetchSourceRowsPaged(connectionId, database, 'oracle', 'oracle-function', sql, 'LINE');
    if (rows.length === 0) return null;
    const body = rows.map((r) => asString(r.text)).join('');
    return body.trim() ? body : null;
  }

  if (objectKind === 'check-constraint') {
    const sql = `SELECT SEARCH_CONDITION AS "body" FROM ALL_CONSTRAINTS
      WHERE OWNER = '${s}' AND CONSTRAINT_NAME = '${n}' AND CONSTRAINT_TYPE = 'C'`;
    const rows = await fetchRows(connectionId, database, sql, 'oracle-check');
    return asString(rows[0]?.body) || null;
  }

  if (objectKind === 'default-constraint') {
    const dot = objectName.name.lastIndexOf('.');
    if (dot < 0) return null;
    const table = escapeSqlLiteral(objectName.name.slice(0, dot));
    const column = escapeSqlLiteral(objectName.name.slice(dot + 1));
    const sql = `SELECT DATA_DEFAULT AS "body" FROM ALL_TAB_COLUMNS
      WHERE OWNER = '${s}' AND TABLE_NAME = '${table}' AND COLUMN_NAME = '${column}'`;
    const rows = await fetchRows(connectionId, database, sql, 'oracle-default');
    return asString(rows[0]?.body) || null;
  }

  return null;
}

// Procedural feed (ProceduralBodyIndexing Slice 4.1). Implementation lives
// in the sibling `oracleProceduralFetcher.ts` to keep this file under the
// 200-line budget — re-exported here so the dispatcher's import path stays
// stable. Package-internal procedures remain a v1.1 follow-up.
export {
  listOracleProceduralObjects,
  fetchOracleProceduralObjectBody,
} from './oracleProceduralFetcher';
